import { Component, h, Prop, State, Host } from '@stencil/core';
import { SuggestResponseItem } from '~model/suggest-response-item';
import { get } from '~service/ajax-service';
import { debounce, DEBOUNCE_DELAY } from '~service/debounce';
import { History, PageLoadTypes } from '~service/history-service';
import { Translate } from '~service/translation-service';

const NO_SUGGESTION_SELECTED = -1;
const MIN_LENGTH = 3;
let uniqueId = 0;

@Component({
  tag: 'staedtler-suggest-box',
})
export class SuggestBox {
  @Prop() placeholder: string = 'search.box.input.placeholder';
  @Prop() searchUrl: string = '';
  @Prop() suggestUrl: string = '';

  @State() q: string = '';
  @State() suggestions: SuggestResponseItem[] = [];
  @State() suggestionsCursor = NO_SUGGESTION_SELECTED;

  uniqueId = uniqueId++;

  fetchSuggestions = debounce(async (q: string): Promise<void> => {
    if (q.length >= MIN_LENGTH) {
      const url = new URL(this.suggestUrl);
      url.searchParams.set('tx_solr[q]', q);
      this.suggestions = await get(url.toString());
    } else {
      this.suggestions = [];
    }
  }, DEBOUNCE_DELAY);

  renderSuggest() {
    if (!this.suggestions.length) {
      return;
    }
    const suggestions = this.suggestions.map((suggestion, index) => {
      const activeState = index === this.suggestionsCursor ? 'active' : 'inactive';
      return (
        <li class={activeState} onMouseDown={_ => this.handleSelect(suggestion.name)} onMouseEnter={_ => this.handleHover(suggestion)}>
          {suggestion.name} ({suggestion.count})
        </li>
      );
    });
    return <ul class="suggest">{suggestions}</ul>;
  }

  handleSubmit(event) {
    event.preventDefault();
    if (this.suggestions[this.suggestionsCursor]) {
      this.handleSelect(this.suggestions[this.suggestionsCursor].name);
    } else {
      this.search(this.q);
    }
    this.suggestions = [];
  }

  handleSelect(name) {
    this.q = name;
    this.search(this.q);
  }

  handleHover(suggestion) {
    this.suggestionsCursor = this.suggestions.indexOf(suggestion);
  }

  handleChange(event) {
    this.q = event.target.value;
    this.fetchSuggestions(this.q);
  }

  handleBlur() {
    this.suggestions = [];
  }

  handleFocus() {
    this.fetchSuggestions(this.q);
  }

  handleKeyDown(event: KeyboardEvent) {
    if (!this.suggestions.length) {
      return;
    }
    switch (event.key.toLocaleLowerCase()) {
      case 'arrowup':
      case 'up':
        if (this.suggestionsCursor <= NO_SUGGESTION_SELECTED) {
          this.suggestionsCursor = this.suggestions.length - 1;
        } else {
          this.suggestionsCursor = Math.max(0, this.suggestionsCursor - 1);
        }
        event.preventDefault();
        break;
      case 'arrowdown':
      case 'down':
        if (this.suggestionsCursor <= NO_SUGGESTION_SELECTED) {
          this.suggestionsCursor = 0;
        } else {
          this.suggestionsCursor = Math.min(this.suggestions.length - 1, this.suggestionsCursor + 1);
        }
        event.preventDefault();
        break;
    }
  }

  render() {
    return (
      <Host>
        <form class="input-group" onSubmit={event => this.handleSubmit(event)}>
          <label htmlFor={'nav-suggest-' + this.uniqueId}>{Translate.translate('search.box.input.label')}</label>
          <input
            class="form-control"
            id={'nav-suggest-' + this.uniqueId}
            type="search"
            autocomplete="off"
            value={this.q}
            placeholder={Translate.translate(this.placeholder)}
            onInput={event => this.handleChange(event)}
            onFocus={_ => this.handleFocus()}
            onBlur={_ => this.handleBlur()}
            onKeyDown={event => this.handleKeyDown(event)}
          />
          <button type="button" class="input-group-addon" aria-label={Translate.translate('search.box.button.submit')} onClick={event => this.handleButton(event)}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#64A8F0">
              <title>Close search</title>
              <path d="M11.088 10l8.687-8.686c.3-.301.3-.787 0-1.089a.772.772 0 0 0-1.09 0L10 8.913 1.314.225a.772.772 0 0 0-1.089 0c-.3.302-.3.788 0 1.09L8.912 10 .225 18.686A.772.772 0 0 0 .77 20a.773.773 0 0 0 .544-.225L10 11.088l8.686 8.687a.773.773 0 0 0 1.089 0c.3-.301.3-.787 0-1.089L11.088 10z"></path>
            </svg>
          </button>
        </form>
        {this.renderSuggest()}
      </Host>
    );
  }

  handleButton(event: MouseEvent) {
    event.preventDefault();
    this.q = '';
    this.closeSuggestion();
  }

  search(q: string) {
    const url = History.addArgumentToUrl(this.searchUrl, 'tx_solr[q]=' + q);
    const addOrRedirect = History.addOrRedirect(url);
    if (addOrRedirect === PageLoadTypes.LOCAL_HISTORY) {
      this.closeSuggestion();
    }
  }

  closeSuggestion() {
    const body = document.querySelector('body');
    body.classList.toggle('searchbox-active');
    body.focus();
  }
}
